'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import CommandCenter from '@/components/CommandCenter';
import NotificationCenter from '@/components/NotificationCenter';
import { supabase } from '@/lib/supabase/client';

interface DashboardData {
  businessName: string;
  businessId: string | null;
  setupProgress: number;
  trademarkRisk: string;
  trademarkStatus: string;
  documentsVerified: number;
  documentsTotal: number;
  companyStatus: string;
  complianceHealth: number;
  isDemo: boolean;
  notifications: Array<{ id: string; title: string; message: string; notification_type: string; action_url: string }>;
  complianceEvents: Array<{ id: string; title: string; due_date: string; status: string; event_type: string }>;
}

const emptyData: DashboardData = {
  businessName: '',
  businessId: null,
  setupProgress: 0,
  trademarkRisk: '—',
  trademarkStatus: 'Not started',
  documentsVerified: 0,
  documentsTotal: 0,
  companyStatus: 'Not started',
  complianceHealth: 0,
  isDemo: false,
  notifications: [],
  complianceEvents: [],
};

export default function DashboardPage() {
  const [data, setData] = useState<DashboardData>(emptyData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [greeting, setGreeting] = useState('Good morning');
  const [commandOpen, setCommandOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour >= 12 && hour < 17) setGreeting('Good afternoon');
    else if (hour >= 17) setGreeting('Good evening');

    async function loadDashboard() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        const [businessRes, notifRes] = await Promise.all([
          supabase
            .from('businesses')
            .select('id, legal_name, setup_progress, is_demo, trademark_searches(risk_level, total_results), compliance_profiles(health_score)')
            .eq('user_id', user.id)
            .limit(1)
            .single(),
          supabase
            .from('notifications')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_read', false)
            .order('created_at', { ascending: false })
            .limit(5),
        ]);

        if (businessRes.error && businessRes.error.code !== 'PGRST116') {
          throw businessRes.error;
        }

        if (businessRes.data) {
          const b = businessRes.data;
          const businessId = b.id;

          // Load compliance events scoped to this business
          const complianceRes = await supabase
            .from('compliance_events')
            .select('*')
            .eq('business_id', businessId)
            .order('due_date', { ascending: true })
            .limit(4);

          // Load documents scoped to this business
          const docsRes = await supabase
            .from('documents')
            .select('id, status')
            .eq('business_id', businessId);

          const docs = docsRes.data || [];
          const verified = docs.filter((d: { status: string }) => d.status === 'verified').length;

          const trademarkSearches = Array.isArray(b.trademark_searches) ? b.trademark_searches : [];
          const latestTM = trademarkSearches[0];
          const complianceProfile = Array.isArray(b.compliance_profiles) ? b.compliance_profiles[0] : null;

          setData({
            businessName: b.legal_name || 'Your Business',
            businessId,
            setupProgress: b.setup_progress || 0,
            trademarkRisk: latestTM?.risk_level || '—',
            trademarkStatus: latestTM ? 'Search complete' : 'Not started',
            documentsVerified: verified,
            documentsTotal: docs.length,
            companyStatus: b.setup_progress >= 100 ? 'Registered' : 'In progress',
            complianceHealth: complianceProfile?.health_score || 0,
            isDemo: b.is_demo || false,
            notifications: notifRes.data || [],
            complianceEvents: complianceRes.data || [],
          });
        } else {
          // No business yet — show empty state
          setData({ ...emptyData, notifications: notifRes.data || [] });
        }
      } catch (err) {
        console.error('Dashboard load error:', err);
        setError('Failed to load dashboard data. Please refresh.');
      } finally {
        setLoading(false);
      }
    }

    loadDashboard();
  }, []);

  const getDaysUntil = (dateStr: string) => {
    const days = Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/dashboard" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-surface-3 rounded-xl w-48" />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[1,2,3,4].map(i => <div key={i} className="h-28 bg-white rounded-xl border border-border" />)}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 h-64 bg-white rounded-xl border border-border" />
                <div className="h-64 bg-white rounded-xl border border-border" />
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/dashboard" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl border border-border p-10 text-center max-w-md w-full">
            <div className="w-14 h-14 bg-danger-light rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-danger" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-ink mb-2">Dashboard unavailable</h2>
            <p className="text-[13px] text-ink-muted mb-6">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-ai text-white text-[13px] font-semibold px-5 py-2.5 rounded-xl hover:bg-ai-dark transition-colors"
            >
              Refresh
            </button>
          </div>
        </main>
      </div>
    );
  }

  const hasNoBusiness = !data.businessId && !data.businessName;

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/dashboard" />
      <CommandCenter open={commandOpen} onClose={() => setCommandOpen(false)} />
      <NotificationCenter open={notifOpen} onClose={() => setNotifOpen(false)} />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-bold text-ink">{greeting}</h1>
                <span className="text-2xl" aria-hidden="true">👋</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {data.businessName && (
                  <p className="text-ink-muted text-[14px]">{data.businessName}</p>
                )}
                {data.isDemo && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-800">
                    DEMO
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setCommandOpen(true)}
                aria-label="Open command search (Ctrl+K)"
                className="hidden md:flex items-center gap-2 px-3 py-2 bg-white border border-border rounded-xl text-[12px] text-ink-muted hover:border-ai/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Search…</span>
                <kbd className="text-[9px] bg-surface-3 border border-border px-1 py-0.5 rounded">⌘K</kbd>
              </button>
              <button
                onClick={() => setNotifOpen(true)}
                aria-label={`Notifications${data.notifications.length > 0 ? ` (${data.notifications.length} unread)` : ''}`}
                className="relative p-2.5 bg-white border border-border rounded-xl text-ink-muted hover:border-ai/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {data.notifications.length > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-danger rounded-full" aria-hidden="true" />
                )}
              </button>
              <Link
                href="/onboarding"
                className="inline-flex items-center gap-2 bg-ai text-white font-semibold px-4 sm:px-5 py-2.5 rounded-xl hover:bg-ai-dark transition-all text-[13px] sm:text-[14px] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2"
              >
                {hasNoBusiness ? 'Create Business' : 'Continue Setup →'}
              </Link>
            </div>
          </div>

          {/* Empty state — no business yet */}
          {hasNoBusiness && (
            <div className="bg-white rounded-2xl border border-border p-10 text-center mb-8">
              <div className="w-16 h-16 bg-ai-light rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-ai" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-ink mb-2">No business yet</h2>
              <p className="text-[13px] text-ink-muted mb-6 max-w-sm mx-auto">
                Create your Business Passport to get started. Lexigen AI will guide you through registration, trademark, and compliance.
              </p>
              <Link
                href="/onboarding"
                className="inline-flex items-center gap-2 bg-ai text-white font-semibold px-6 py-3 rounded-xl hover:bg-ai-dark transition-all text-[14px] shadow-sm"
              >
                Create Business Passport →
              </Link>
            </div>
          )}

          {/* Status Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
            <div className="bg-white rounded-xl border border-border p-4 sm:p-5">
              <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-3">Business Setup</p>
              <div className="flex items-end gap-2 mb-3">
                <span className="text-2xl font-bold text-ink">{data.setupProgress}%</span>
                <span className="text-[12px] text-ink-muted mb-0.5">complete</span>
              </div>
              <div className="w-full bg-surface-3 rounded-full h-1.5" role="progressbar" aria-valuenow={data.setupProgress} aria-valuemin={0} aria-valuemax={100}>
                <div className="bg-ai h-1.5 rounded-full transition-all" style={{ width: `${data.setupProgress}%` }} />
              </div>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 sm:p-5">
              <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-3">Trademark</p>
              <p className="text-[13px] text-ink-muted mb-1">{data.trademarkStatus}</p>
              {data.trademarkRisk !== '—' && (
                <span className={`text-[12px] font-semibold px-2 py-0.5 rounded-full ${
                  data.trademarkRisk === 'low' ? 'bg-green-50 text-green-700' :
                  data.trademarkRisk === 'high' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
                }`}>
                  Risk: {data.trademarkRisk}
                </span>
              )}
            </div>

            <div className="bg-white rounded-xl border border-border p-4 sm:p-5">
              <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-3">Documents</p>
              <div className="flex items-end gap-1 mb-3">
                <span className="text-2xl font-bold text-ink">{data.documentsVerified}</span>
                <span className="text-[14px] text-ink-muted mb-0.5">/ {data.documentsTotal || '—'}</span>
              </div>
              <p className="text-[12px] text-ink-muted">verified</p>
            </div>

            <div className="bg-white rounded-xl border border-border p-4 sm:p-5">
              <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-3">Compliance</p>
              <div className="flex items-end gap-1 mb-2">
                <span className="text-2xl font-bold text-ink">{data.complianceHealth}</span>
                <span className="text-[14px] text-ink-muted mb-0.5">/ 100</span>
              </div>
              <span className={`text-[12px] font-semibold ${
                data.complianceHealth >= 80 ? 'text-green-700' :
                data.complianceHealth >= 50 ? 'text-amber-700' : 'text-red-700'
              }`}>
                {data.complianceHealth >= 80 ? 'Healthy' : data.complianceHealth >= 50 ? 'Needs attention' : data.complianceHealth > 0 ? 'At risk' : 'Not set up'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">

              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-border p-5 sm:p-6">
                <h2 className="font-semibold text-ink text-[15px] mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  {[
                    { label: 'Continue Company Registration', href: '/onboarding', icon: '🏢', badge: `${data.setupProgress}% done` },
                    { label: 'Review Trademark Risk', href: '/trademark', icon: '™', badge: data.trademarkRisk !== '—' ? `${data.trademarkRisk} risk` : 'Start search' },
                    { label: 'Upload Missing Documents', href: '/documents', icon: '📄', badge: data.documentsTotal > 0 ? `${data.documentsTotal - data.documentsVerified} pending` : 'View docs' },
                    { label: 'Check Compliance', href: '/compliance', icon: '✅', badge: data.complianceHealth > 0 ? `${data.complianceHealth}/100` : 'Set up' },
                  ].map((action, i) => (
                    <Link
                      key={i}
                      href={action.href}
                      className="flex items-center justify-between p-4 rounded-xl border border-border hover:border-ai/30 hover:bg-surface-2 transition-all group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl w-8 text-center" aria-hidden="true">{action.icon}</span>
                        <span className="text-[13px] font-medium text-ink">{action.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold bg-surface-3 text-ink-muted px-2 py-0.5 rounded-full">{action.badge}</span>
                        <svg className="w-4 h-4 text-ink-subtle group-hover:text-ai transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Compliance Events */}
              {data.complianceEvents.length > 0 && (
                <div className="bg-white rounded-xl border border-border p-5 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-ink text-[15px]">Upcoming Compliance</h2>
                    <Link href="/compliance" className="text-[12px] text-ai hover:text-ai-dark font-medium transition-colors">View all →</Link>
                  </div>
                  <div className="space-y-3">
                    {data.complianceEvents.map(event => {
                      const days = getDaysUntil(event.due_date);
                      return (
                        <div key={event.id} className={`flex items-center justify-between p-3 rounded-xl border ${
                          event.status === 'overdue' ? 'border-danger/30 bg-danger/[0.02]' :
                          event.status === 'due_soon' ? 'border-warning/30 bg-warning/[0.02]' :
                          'border-border'
                        }`}>
                          <div>
                            <p className="text-[13px] font-semibold text-ink">{event.title}</p>
                            <p className="text-[11px] text-ink-muted mt-0.5">
                              {days < 0 ? `${Math.abs(days)} days overdue` : days === 0 ? 'Due today' : `Due in ${days} days`}
                            </p>
                          </div>
                          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                            event.status === 'overdue' ? 'bg-red-50 text-red-700' :
                            event.status === 'due_soon'? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-700'
                          }`}>
                            {event.status === 'overdue' ? 'Overdue' : event.status === 'due_soon' ? 'Due soon' : 'On track'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* AI Assistant CTA */}
              <div
                className="rounded-xl p-5 text-white"
                style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 bg-white/15 rounded-lg flex items-center justify-center">
                    <span className="text-white text-[11px] font-bold">AI</span>
                  </div>
                  <span className="text-[13px] font-bold text-white">Lexigen AI</span>
                </div>
                <p className="text-[12px] text-white/70 mb-4 leading-relaxed">
                  Ask anything about your business — registrations, compliance, trademark, or next steps.
                </p>
                <Link
                  href="/assistant"
                  className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white text-[12px] font-semibold px-4 py-2 rounded-lg transition-all border border-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
                >
                  Ask Lexigen AI →
                </Link>
              </div>

              {/* Notifications */}
              {data.notifications.length > 0 && (
                <div className="bg-white rounded-xl border border-border p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-ink text-[15px]">Notifications</h2>
                    <span className="text-[11px] font-bold bg-danger/10 text-danger px-2 py-0.5 rounded-full">
                      {data.notifications.length} new
                    </span>
                  </div>
                  <div className="space-y-3">
                    {data.notifications.slice(0, 3).map(notif => (
                      <Link
                        key={notif.id}
                        href={notif.action_url || '/dashboard'}
                        className="flex items-start gap-3 p-3 rounded-xl hover:bg-surface-2 transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
                      >
                        <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                          notif.notification_type === 'warning' ? 'bg-amber-500' :
                          notif.notification_type === 'danger' ? 'bg-danger' : 'bg-ai'
                        }`} aria-hidden="true" />
                        <div>
                          <p className="text-[12px] font-semibold text-ink">{notif.title}</p>
                          <p className="text-[11px] text-ink-muted mt-0.5">{notif.message}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty notifications */}
              {data.notifications.length === 0 && !hasNoBusiness && (
                <div className="bg-white rounded-xl border border-border p-5 text-center">
                  <div className="w-10 h-10 bg-surface-3 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <svg className="w-5 h-5 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <p className="text-[12px] text-ink-muted">All caught up!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
